// Generated by GitHub Copilot
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Alert } from "react-bootstrap";
import { impactService } from "../../services/impactService";
import ImpactTableView from "../Impact/ImpactTableView";
import ImpactFormModal from "../Impact/ImpactFormModal";
import ConfirmModal from "../common/ConfirmModal";
import styles from "./ImpactReportsViewer.module.css";
import usePolling from "../../hooks/usePolling";
import { POLLING_INTERVALS } from "../../config/polling";

/**
 * ImpactReportsViewer - Displays impact reports in a compact table view
 * Used in both SinglePane and TabbedViewer components
 */
function ImpactReportsViewer({ problemNumber, user, refreshTrigger }) {
  const [impacts, setImpacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for table interaction
  const [expandedRows, setExpandedRows] = useState(new Set());

  // State for edit modal
  const [editingImpact, setEditingImpact] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // State for resolve modal
  const [resolvingImpact, setResolvingImpact] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);

  // State for API operations
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const currentUserEmail = user?.email || "";

  const fetchImpacts = useCallback(async (isBackgroundRefresh = false) => {
    if (!problemNumber) {
      if (!isBackgroundRefresh) setLoading(false);
      return;
    }

    try {
      if (!isBackgroundRefresh) {
        setLoading(true);
      }

      // Fetch all impacts (both active and resolved)
      const data = await impactService.getImpacts(problemNumber);

      setImpacts(data.impacts || []);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch impact reports:", err);
      if (isBackgroundRefresh) {
        // Re-throw so usePolling can trigger its backoff mechanism
        throw err;
      } else {
        setError("Failed to load impact reports");
      }
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    }
  }, [problemNumber]);

  // Initial load when the problem changes
  useEffect(() => {
    fetchImpacts();
  }, [fetchImpacts]);

  // Silent refresh when the current user submits a new impact (manual trigger from parent)
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchImpacts(true).catch(() => {});
    }
  }, [refreshTrigger, fetchImpacts]);

  // Background auto-refresh — pauses when the tab is hidden to avoid wasted requests
  usePolling(
    () => fetchImpacts(true),
    POLLING_INTERVALS.IMPACT_REPORTS,
    { pauseWhenHidden: true }
  );

  // Auto-dismiss success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Toggle row expansion
  const toggleExpand = useCallback((impactId) => {
    setExpandedRows((prevExpanded) => {
      const newExpanded = new Set(prevExpanded);
      if (newExpanded.has(impactId)) {
        newExpanded.delete(impactId);
      } else {
        newExpanded.add(impactId);
      }
      return newExpanded;
    });
  }, []);

  // Handle edit action
  const handleEdit = useCallback((impact) => {
    setError(null); // Clear any previous error
    setEditingImpact(impact);
    setShowEditModal(true);
  }, []);

  // Handle save edit
  const handleSaveEdit = async (impactData) => {
    if (!editingImpact) return;

    setSubmitting(true);
    try {
      await impactService.updateImpact(
        problemNumber,
        editingImpact.id,
        impactData,
      );
      setShowEditModal(false);
      setEditingImpact(null);
      setSuccessMessage("Impact updated successfully");

      // Refresh impacts
      const data = await impactService.getImpacts(problemNumber);
      setImpacts(data.impacts || []);
    } catch (err) {
      console.error("Failed to update impact:", err);
      setError("Failed to update impact");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle resolve action
  const handleResolve = useCallback((impact) => {
    setError(null); // Clear any previous error
    setResolvingImpact(impact);
    setShowResolveModal(true);
  }, []);

  // Confirm resolve
  const confirmResolve = async () => {
    if (!resolvingImpact) return;

    setSubmitting(true);
    try {
      // Update impact status to "resolved" - include all existing fields
      await impactService.updateImpact(problemNumber, resolvingImpact.id, {
        ...resolvingImpact,
        status: "resolved",
      });
      setShowResolveModal(false);
      setResolvingImpact(null);
      setSuccessMessage("Impact marked as resolved");

      // Refresh impacts
      const data = await impactService.getImpacts(problemNumber);
      setImpacts(data.impacts || []);
    } catch (err) {
      console.error("Failed to resolve impact:", err);
      setError("Failed to resolve impact");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.viewerContainer}>
      {/* Success Message */}
      {successMessage && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && !loading && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table View */}
      <ImpactTableView
        impacts={impacts}
        currentUserEmail={currentUserEmail}
        expandedRows={expandedRows}
        onToggleExpand={toggleExpand}
        onEdit={handleEdit}
        onResolve={handleResolve}
        loading={loading}
        error={null} // Error shown above
      />

      {/* Edit Modal */}
      <ImpactFormModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditingImpact(null);
        }}
        onSubmit={handleSaveEdit}
        initialValues={editingImpact}
        loading={submitting}
        problemNumber={problemNumber}
        title="Edit Impact Report"
      />

      {/* Resolve Confirmation Modal */}
      <ConfirmModal
        show={showResolveModal}
        onHide={() => {
          setShowResolveModal(false);
          setResolvingImpact(null);
        }}
        onConfirm={confirmResolve}
        title="Resolve Impact"
        message={`Are you sure you want to mark this impact as resolved? This will change the status from "active" to "resolved".`}
        confirmText="Mark as Resolved"
        cancelText="Cancel"
        confirmVariant="success"
        loading={submitting}
      />
    </div>
  );
}

ImpactReportsViewer.propTypes = {
  problemNumber: PropTypes.string.isRequired,
  user: PropTypes.shape({
    privileges: PropTypes.shape({
      impact: PropTypes.bool,
      editor: PropTypes.bool,
    }),
  }),
  // Generated by GitHub Copilot - Trigger to refresh impact list
  refreshTrigger: PropTypes.number,
};

ImpactReportsViewer.defaultProps = {
  user: null,
  // Generated by GitHub Copilot
  refreshTrigger: 0,
};

export default ImpactReportsViewer;
